import axios from 'axios';
import {filter} from "../filter/filter";

const DATA_ATTR = 'data-notes';
const CLASS_PREFIX = 'notes';
const ACTIVE_CLASS = `${CLASS_PREFIX}__group--active`;
const STORAGE_KEY_PREFIX = `${DATA_ATTR}-filters`; // will store under this key in localStorage

const $container = document.querySelector(`[${DATA_ATTR}]`);

if ($container) notes($container);

export function notes($container) {

    const $notesGroupTemplate = $container.querySelector(`[${DATA_ATTR}-template-notes-group]`);
    const $noteTemplate = $container.querySelector(`[${DATA_ATTR}-template-note]`);

    const $filters = $container.querySelector(`[${DATA_ATTR}-filters]`);
    const $filtersContainer = $container.querySelector(`[${DATA_ATTR}-filters-container]`);
    const $filterSearch = $container.querySelector(`[${DATA_ATTR}-filter-search]`);
    const $list = $container.querySelector(`[${DATA_ATTR}-list]`);
    const $loader = $container.querySelector(`[${DATA_ATTR}-loader]`);

    let currentFilters = {};
    let groupData = []; // raw data from server (groups with notes)
    const groups = [];
    let currentSearch = ''; // current search query (not persisted by default)

    // IntersectionObserver с достаточной детализацией thresholds
    const io = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            // сохраняем ratio на таргете
            entry.target._ioRatio = entry.intersectionRatio;
        });
        evaluateActive();
    }, {
        root: null,
        // много промежуточных порогов — чтобы получить адекватные ratio при скролле
        threshold: Array.from({length: 101}, (_, i) => i / 100)
    });

    init();

    async function init() {
        console.log('notes init');
        await loadFiltersFromStorage();
        await fetchFilters();
        await fetchNotes();

        // init search input (debounced)
        if ($filterSearch) {
            $filterSearch.addEventListener('input', debounce((e) => {
                const v = (e.target.value || '').trim();
                currentSearch = v;
                // We don't persist search to storage by default
                applySearchAndRender();
            }, 300));

            // allow clearing search with Escape
            $filterSearch.addEventListener('keydown', (e) => {
                if (e.key === 'Escape') {
                    $filterSearch.value = '';
                    currentSearch = '';
                    applySearchAndRender();
                    $filterSearch.blur();
                }
            });
        }
    }

    async function fetchFilters() {
        try {
            const response = await axios.get('/api/notes/filters');
            const filtersData = response.data && response.data.data ? response.data.data : [];
            await renderFilters(filtersData);
        } catch (error) {
            console.error('fetchFilters error:', error);
        } finally {

        }
    }

    async function fetchNotes() {
        $loader.style.display = 'flex';

        const params = {
            filters: JSON.stringify(currentFilters || {})
        };

        axios.get('/api/notes', {params})
            .then(response => {
                if (!response.data) return;
                // ожидаем, что сервер возвращает массив групп: [{ title, notes: [{id,title,description,category,tags:[{name}]}, ...] }, ...]
                groupData = Array.isArray(response.data) ? response.data : (response.data.data || []);
                // после получения данных применяем текущий поиск и рендерим
                applySearchAndRender();
            }).catch(error => {
            console.error(error);
        }).finally(() => {
            $loader.style.display = 'none';
        });
    }

    async function renderFilters(filtersData = []) {
        console.log('filtersData', filtersData)

        $filtersContainer.innerHTML = '';

        // helper: безопасно получить display name для айтема
        const itemName = (item) => {
            if (!item) return 'ID unknown';
            return item.name || item.title || (item.first_name || item.last_name ? `${item.first_name || ''} ${item.last_name || ''}`.trim() : item.email) || `ID ${item.id ?? 'unknown'}`;
        };

        // Пройтись по полученным фильтрам и создать каждый list-фильтр
        filtersData.forEach(filterData => {
            // ожидаем структуру { entity: 'Группы учителя', meta: 'university_groups', items: [{id, name}, ...] }
            const entityTitle = filterData.entity || filterData.meta || 'Фильтр';
            const metaName = filterData.meta || filterData.entity || 'unknown_meta';
            const itemsRaw = Array.isArray(filterData.items) ? filterData.items : [];

            const items = itemsRaw.map(i => ({
                id: i.id,
                name: itemName(i),
            }));

            // get current saved value for this meta (array of ids) — default empty array = "все"
            const initialValue = Array.isArray(currentFilters[metaName]) ? currentFilters[metaName] : [];

            filter($filtersContainer, {
                type: 'list',
                filterFor: metaName,                 // мета-имя таблицы/сущности
                title: `Все ${entityTitle}`,        // заголовок (можно изменить)
                name: entityTitle,                  // читаемое имя фильтра
                data: items.length ? items : [],    // массив айтемов {id, name}
                value: initialValue, // начальное значение (пустой = "все")
                onAccept: (val) => {
                    console.log('Приняли фильтр:', metaName, val);
                    currentFilters[metaName] = Array.isArray(val) ? val : [];
                    saveFiltersToStorage();
                    fetchNotes();
                },
                onClear: () => {
                    console.log('Сброс фильтра:', metaName);
                    delete currentFilters[metaName];
                    saveFiltersToStorage();
                    fetchNotes();
                },
                onChange: (val) => {
                    console.log('Изменилось:', metaName, val);
                    currentFilters[metaName] = Array.isArray(val) ? val : [];
                    saveFiltersToStorage();
                },
            });
        });
    }

    function renderList(groupsData) {
        // если перерисовываем — сначала отписываемся и чистим
        groups.forEach(g => io.unobserve(g));
        groups.length = 0;

        $list.innerHTML = '';

        const groupsArr = Array.isArray(groupsData) ? groupsData : [];
        const totalNotes = groupsArr.reduce((acc, g) => acc + (Array.isArray(g.notes) ? g.notes.length : 0), 0);
        if (totalNotes === 0) {
            const $empty = document.createElement('div');
            $empty.setAttribute(`${DATA_ATTR}-empty`, '');
            $empty.className = `${CLASS_PREFIX}__empty`;
            // для доступности: уведомляем экранные читалки
            $empty.setAttribute('role', 'status');
            $empty.setAttribute('aria-live', 'polite');

            $empty.innerHTML = `
            <p class="${CLASS_PREFIX}__empty-text">
                По выбранным фильтрам техники еще в разработке. Совсем скоро они появятся в каталоге.
            </p>
        `;
            $list.appendChild($empty);
            return;
        }

        groupsArr.forEach(group => {
            renderNotes($list, group);
        });
    }

    function renderNotes($groupContainer, groupData) {
        const $notesGroupClone = document.importNode($notesGroupTemplate.content, true);
        const $notesGroup = $notesGroupClone.querySelector(`[${DATA_ATTR}-container]`);
        const $notesGroupHeader = $notesGroupClone.querySelector(`[${DATA_ATTR}-header]`);
        const $notesGroupList = $notesGroupClone.querySelector(`[${DATA_ATTR}-list]`);

        $notesGroupHeader.textContent = groupData.title;
        (Array.isArray(groupData.notes) ? groupData.notes : []).forEach(note => {
            renderNote($notesGroupList, note);
        })

        // Делаем группу доступной по табу + вешаем обработчики
        $notesGroup.setAttribute('tabindex', '0');

        // hover / pointer
        $notesGroup.addEventListener('pointerenter', () => {
            $notesGroup._hover = true;
            evaluateActive();
        });
        $notesGroup.addEventListener('pointerleave', () => {
            $notesGroup._hover = false;
            evaluateActive();
        });

        // focus / blur (клавиатурная навигация)
        $notesGroup.addEventListener('focus', () => {
            $notesGroup._focused = true;
            evaluateActive();
        });
        $notesGroup.addEventListener('blur', () => {
            $notesGroup._focused = false;
            evaluateActive();
        });

        // Вставляем в DOM (event listeners уже привязаны к узлу)
        $groupContainer.appendChild($notesGroupClone);

        // Теперь $notesGroup уже в DOM — начинаем наблюдать
        groups.push($notesGroup);
        io.observe($notesGroup);
    }

    function renderNote($noteContainer, noteData) {
        const $noteClone = document.importNode($noteTemplate.content, true);
        const $noteCard = $noteClone.querySelector(`[${DATA_ATTR}-card]`);
        const $noteTitle = $noteClone.querySelector(`[${DATA_ATTR}-title]`);
        const $noteText = $noteClone.querySelector(`[${DATA_ATTR}-text]`);
        const $noteTags = $noteClone.querySelector(`[${DATA_ATTR}-tags]`);
        const $noteCategory = $noteClone.querySelector(`[${DATA_ATTR}-category]`);

        $noteCard.href = '/notes/' + (noteData.id ?? '');
        $noteTitle.textContent = noteData.title || '';
        $noteText.textContent = noteData.description || '';
        $noteCategory.textContent = noteData.category || '';
        ($noteTags && Array.isArray(noteData.tags) ? noteData.tags : []).forEach(tag => {
            renderTag($noteTags, tag);
        })

        $noteContainer.appendChild($noteClone);
    }

    function renderTag($tagContainer, tag) {
        const $tag = document.createElement('div');
        $tag.setAttribute(`${DATA_ATTR}-tag`, '');
        $tag.className = `${CLASS_PREFIX}__note-tag ${CLASS_PREFIX}__note-tag--blue`;
        $tag.textContent = tag && tag.name ? tag.name : (tag || '');
        $tagContainer.appendChild($tag);
    }

    // ---------- логика выбора активного блока ----------
    function evaluateActive() {
        // 1) Hover имеет приоритет
        const hovered = groups.find(g => g._hover);
        if (hovered) {
            setActive(hovered);
            return;
        }

        // 2) Фокус клавиатуры
        const activeElement = document.activeElement;
        if (activeElement && groups.includes(activeElement)) {
            setActive(activeElement);
            return;
        }
    }

    function setActive(el) {
        groups.forEach(g => {
            if (g === el) {
                g.classList.add(ACTIVE_CLASS);
            } else {
                g.classList.remove(ACTIVE_CLASS);
            }
        });
    }

    // ---------- Filter persistence helpers ----------
    function storageKey() {
        // убрал неизвестный `type` — сохраняем под единым ключом
        return `${STORAGE_KEY_PREFIX}`;
    }

    function saveFiltersToStorage() {
        try {
            const key = storageKey();
            localStorage.setItem(key, JSON.stringify(currentFilters || {}));
        } catch (e) {
            // ignore storage errors
            console.warn('Could not save filters to localStorage', e);
        }
    }

    async function loadFiltersFromStorage() {
        try {
            const key = storageKey();
            const raw = localStorage.getItem(key);
            if (!raw) {
                currentFilters = {};
                return {};
            }
            const parsed = JSON.parse(raw);
            currentFilters = parsed && typeof parsed === 'object' ? parsed : {};
            return currentFilters;
        } catch (e) {
            console.warn('Could not load filters from localStorage', e);
            currentFilters = {};
            return {};
        }
    }

    // ---------- Search helpers ----------
    function applySearchAndRender() {
        const filtered = getFilteredGroupsBySearch(groupData, currentSearch);
        renderList(filtered);
    }

    function getFilteredGroupsBySearch(groupsArr, search) {
        if (!search || !search.length) {
            // return deep copy? not necessary — renderList will read data and clone templates
            return Array.isArray(groupsArr) ? groupsArr : [];
        }
        const s = search.toLowerCase();

        // фильтруем внутри групп — оставляем только заметки, которые совпадают по title/description/tags/category
        const result = [];
        (Array.isArray(groupsArr) ? groupsArr : []).forEach(group => {
            const matchedNotes = (Array.isArray(group.notes) ? group.notes : []).filter(note => {
                const title = (note.title || '').toLowerCase();
                const category = (note.category || '').toLowerCase();
                const tags = (Array.isArray(note.tags) ? note.tags.map(t => (t.name || '').toLowerCase()).join(' ') : '');
                return title.includes(s) || category.includes(s) || tags.includes(s);
            });

            if (matchedNotes.length) {
                // копируем группу с отфильтрованными заметками
                result.push({
                    ...group,
                    notes: matchedNotes
                });
            }
        });

        return result;
    }

    // ---------- Utilities ----------
    function debounce(fn, wait = 200) {
        let t = null;
        return function (...args) {
            clearTimeout(t);
            t = setTimeout(() => fn.apply(this, args), wait);
        }
    }

}
